import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button } from 'antd'
import classnames from 'classnames'
import congratsIconEN from '../img/ic-congrats-wlc-en.png'
import congratsIconES from '../img/ic-congrats-wlc-es.png'
import congratsIconPT from '../img/ic-congrats-wlc-pt.png'
import congratsIconFR from '../img/ic-congrats-wlc-fr.png'
import congratsIconDE from '../img/ic-congrats-wlc-de.png'
import congratsMskIcon from '../img/ic-eligible-msk.png'
import congratsWlcMskIcon from '../img/ic-eligible-wlc-msk.png'
import { useI18n, useUserProfile } from 'contexts/AuthContext'
import ContentHeader from '../ContentHeader'
import styles from './eligible.module.scss'
import { useMCA } from './useMCA'
import { logAmplitudeEvent } from 'utils/amplitudeInit'
import _, { isEmpty } from 'lodash'
import { LoadingContent } from 'components/LoadingDialog/index'
import { getFitOnHost } from 'apis/formatUrl'
import { mcaScreenerPromise, updateUserPromise } from 'apis/FitOnCareAPI'
import { useMetadataOptions } from 'hooks/careHooks'
import { useSkuCondition } from 'hooks/useSkuCondiction'
import { ScreenKey } from '../util'
import { getAutoLoginParams } from 'utils/basic'
import { getUserEmail } from 'utils/authLocalStorage'

const EligibleInfo = [
  {
    type: 'eckm',
    title: `You're approved. <br /> Welcome to FitOn Care!`,
    subtitle: `Your personalized program for metabolic health — covered by Medicare.`,
    description: `By enrolling, you agree to the following:`,
    items: [
      'Participation is voluntary and 100% paid by Medicare -- free to you',
      'FitOn Care will be the program provider',
      'You may unenroll any time after 90 days',
    ],
    icons: {
      en: congratsIconEN,
      es: congratsIconES,
      pt: congratsIconPT,
      fr: congratsIconFR,
      de: congratsIconDE,
    },
  },
  {
    type: 'msk',
    title: `You're approved. <br /> Welcome to FitOn Care!`,
    subtitle: `Your personalized program to reduce joint and muscle pain — covered by Medicare.`,
    description: `By enrolling, you agree to the following:`,
    items: [
      'Participation is voluntary and 100% paid by Medicare -- free to you',
      'FitOn Care will be the program provider',
      'You may unenroll any time after 90 days',
    ],
    icons: {
      en: congratsMskIcon,
      es: congratsMskIcon,
      pt: congratsMskIcon,
      fr: congratsMskIcon,
      de: congratsMskIcon,
    },
  },
  {
    type: 'both',
    title: `You're approved. <br /> Welcome to FitOn Care!`,
    subtitle: `Your program for metabolic, joint, and muscle health — covered by Medicare.`,
    description: `By enrolling, you agree to the following:`,
    items: [
      'Participation is voluntary and 100% paid by Medicare -- free to you',
      'FitOn Care will be the program provider',
      'You may unenroll any time after 90 days',
    ],
    icons: {
      en: congratsWlcMskIcon,
      es: congratsWlcMskIcon,
      pt: congratsWlcMskIcon,
      fr: congratsWlcMskIcon,
      de: congratsWlcMskIcon,
    },
  },
]
const Eligible = (props) => {
  const { sku, setCurrentKey } = props
  const { i18n, localCode } = useI18n()
  const { alignTracks, getMcaEnrollment } = useMCA({ sku })
  const [candidateTracks, setCandidateTracks] = useState([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [mcaBodyAreas, setMcaBodyAreas] = useState([])
  const { mcaMskBodyAreas } = useMetadataOptions()
  const { getNewSkuCondition } = useSkuCondition()
  const { skuCondition } = useUserProfile()
  const [currentFitonUserId, setCurrentFitonUserId] = useState()
  const eventTrackRef = useRef(null)
  const [enrollment, setEnrollment] = useState(null)

  const getMcaInfo = useCallback(async () => {
    const [enrollment, mcaInfo] = await Promise.all([
      getMcaEnrollment(),
      mcaScreenerPromise(),
    ])
    const candidateTracks = enrollment?.enrollment?.candidateTracks
    setCandidateTracks(candidateTracks)
    setEnrollment(enrollment?.enrollment || {})

    const mcaBodyAreas = mcaInfo.data?.bodyAreas
    const fitonUserId = mcaInfo.data?.fitonUserId?.toString()
    setCurrentFitonUserId(fitonUserId)
    setMcaBodyAreas(
      mcaBodyAreas?.map((area) => _.find(mcaMskBodyAreas, { value: area })) ||
        []
    )
  }, [getMcaEnrollment, mcaMskBodyAreas])

  useEffect(() => {
    getMcaInfo()
  }, [getMcaInfo])

  const onBack = (e) => {
    props.setRemoveEventListener(false)
    props.onBack(e)
  }

  useEffect(() => {
    if (!_.isEmpty(candidateTracks) && !eventTrackRef.current) {
      eventTrackRef.current = true
      logAmplitudeEvent('Screen View: Onboarding - Eligible', {
        sku,
        tracks:
          candidateTracks?.length === 2
            ? 'both'
            : candidateTracks?.length === 1
              ? candidateTracks[0]
              : '',
      })
    }
  }, [sku, candidateTracks])

  const onAccept = async () => {
    logAmplitudeEvent('Click: Onboarding - Eligible - Accept', {
      sku,
      selected_tracks: candidateTracks,
    })
    if (enrollment?.status === 'aligned') {
      setCurrentKey(ScreenKey.MCA_PRIMARY_CARE_PROVIDER)
    } else {
      setIsSubmitting(true)
      const nextScreenKey = await alignTracks({
        acceptedTracks: candidateTracks,
        participationVoluntary: true,
        oneParticipantPerTrack: true,
        mayDisenrollAfter90Days: true,
        costSharingAcknowledged: true,
      })
      setCurrentKey(nextScreenKey)
      setIsSubmitting(false)
      if (nextScreenKey === ScreenKey.MCA_PRIMARY_CARE_PROVIDER) {
        const newSkuCondition = getNewSkuCondition({
          skuCondition: skuCondition,
          themeIds: mcaBodyAreas?.map((area) => area.id),
          sku,
        })
        updateUserPromise({
          skuCondition: newSkuCondition,
        })
      }
    }
  }
  const onDecline = () => {
    logAmplitudeEvent('Click: Onboarding - Eligible - Decline', {
      sku,
    })
    window.location.href = `${getFitOnHost()}?${getAutoLoginParams({ fitOnUserId: currentFitonUserId, fitOnEmail: getUserEmail() })}`
  }

  const { title, subtitle, description, items, icons, tip } = useMemo(() => {
    let type
    if (candidateTracks?.length === 2) {
      type = 'both'
    } else if (candidateTracks?.length === 1) {
      type = candidateTracks[0]
    } else {
      type = ''
    }
    return EligibleInfo.find((item) => item.type === type) || {}
  }, [candidateTracks])

  const icon = useMemo(() => {
    return icons?.[localCode]
  }, [icons, localCode])

  return (
    <div
      className={classnames('info-sec', styles.styles)}
      style={{ minHeight: '100%' }}
    >
      {isEmpty(candidateTracks) ? (
        <div className='loading'>
          <LoadingContent />
        </div>
      ) : (
        <>
          <ContentHeader {...props} onBack={onBack} hideBack={true} />
          <div className='info-content'>
            <img
              src={icon}
              alt=''
              className='top-icon'
              style={{
                width: '327px',
                margin: '50px auto 0',
                borderRadius: '8px',
                objectFit: 'cover',
                objectPosition: 'center',
                height: 'auto',
                maxHeight: '160px',
              }}
            />
            <div
              className='title-pns'
              dangerouslySetInnerHTML={{ __html: i18n(title) }}
            />
            {subtitle && (
              <div
                className='subtitle'
                style={{
                  margin: '16px 0 0',
                  textAlign: 'left',
                  fontFamily: 'AktivGrotesk-bold',
                }}
              >
                {i18n(subtitle)}
              </div>
            )}
            <div className='description'>{i18n(description)}</div>
            <ul className='ul'>
              {items?.map((item, index) => (
                <li className='li' key={index}>
                  {i18n(item)}
                </li>
              ))}
            </ul>
            {tip && <div className='tip'>{i18n(tip)}</div>}
            <Button
              className='btn-primary full-width-btn'
              style={{ marginTop: '32px', width: '100%' }}
              onClick={onAccept}
              loading={isSubmitting}
            >
              {i18n('Enroll')}
            </Button>
            <button className='decline-btn' onClick={onDecline}>
              {i18n('Cancel')}
            </button>
          </div>
        </>
      )}
    </div>
  )
}

export default Eligible
